/**
 * GNK 2026 본예산 지출편성 이관 부트스트랩
 * - 데이터: gnkExpenseRequests.data.js (기존 시스템 bbudmst/bbuddtl 추출)
 * - 부서·과목·사업 코드는 이미 등록된 마스터에 이름으로 매핑한다.
 *   · 과목: 세항명>목명>세목명 체인 매칭 (실패 시 목>세목 → 비고 기록)
 *   · 사업: 레거시 사업명(=목명)과 사업등록 명칭 정규화 비교 — 미매핑은 담당자 지정
 * - 편성요구 저장소가 비어 있을 때 1회만 적재한다.
 *
 * 재생성: node scripts/buildGnkExpenseRequestSeed.js
 */
import {
  loadExpenseRequests,
  saveExpenseRequests,
  createRequestUid,
  createDetailId,
} from '../budgetExpenseRequestStore';
import { loadSubjectsByFilter, BUDGET_TYPE_ALL, STRUCTURE_BUDGET_TYPE } from '../budgetStructureStore';
import { buildSubjectDispCode, buildSubjectDispName } from '../budgetSubjectHistoryStore';
import { loadProjects } from '../budgetProjectStore';
import { loadFunds } from '../budgetFundStore';
import { loadDepts } from '../../master/deptStore';
import { defaultAcctClassCode } from '../budgetAcctStore';
import { MAIN_KIND_CODE } from '../budgetRoundStore';
import { GNK_EXPENSE_REQUEST_ROWS } from './gnkExpenseRequests.data';

export const GNK_EXPENSE_REQUEST_SOURCE = 'GNK 2026 본예산 이관';
export const GNK_EXPENSE_FISCAL_YEAR = '2026';

/** 사업명 매핑 실패 시 일괄 지정할 기본 사업 (담당자 합의) */
const FALLBACK_PROJECT_NAME = '학대피해아동 회복 및 가정역량강화지원사업';
/** 레거시 재원은 산출기초에 코드 직접 선택 방식이라 이관 제외 — 기본 재원으로 일괄 지정 */
const DEFAULT_FUND_NAME = '국비';

export function isGnkTenant(tenantCode) {
  return String(tenantCode || '').trim().toUpperCase() === 'GNK';
}

function norm(name) {
  return String(name || '').replace(/\s+/g, '').trim();
}

/** 사업명 비교용 — 공백·콤마·가운뎃점 제거 + 말미 "(예산)" 제거 */
function normProjectName(name) {
  return norm(name).replace(/[,·]/g, '').replace(/\(예산\)$/, '');
}

/** 최종과목(세목) → 세항|목|세목 이름 체인 매핑 테이블 */
function buildSubjectChainMaps(subjects) {
  const byCode = new Map();
  subjects.forEach((r) => {
    byCode.set(`${r.budgetType || ''}::${r.subjectCode}`, r);
    if (!byCode.has(`::${r.subjectCode}`)) byCode.set(`::${r.subjectCode}`, r);
  });

  const chainOf = (row) => {
    const chain = { 세항: '', 목: '', 세목: '' };
    let cur = row;
    let guard = 0;
    while (cur && guard++ < 12) {
      if (cur.levelName === '세항' || cur.levelName === '목' || cur.levelName === '세목') {
        chain[cur.levelName] = norm(cur.subjectName);
      }
      if (!cur.parentCode) break;
      cur =
        byCode.get(`${cur.budgetType || ''}::${cur.parentCode}`) ||
        byCode.get(`::${cur.parentCode}`) ||
        null;
    }
    return chain;
  };

  const fullMap = new Map(); // 세항|목|세목
  const mokMap = new Map(); // 목|세목
  const semokMap = new Map(); // 세목 단독 (유일할 때만 사용)
  subjects
    .filter((r) => r.finalYn === 'Y' && r.useYn !== 'N' && r.mod !== 'D')
    .forEach((r) => {
      const chain = chainOf(r);
      if (!chain.세목) return;
      const fullKey = `${chain.세항}|${chain.목}|${chain.세목}`;
      if (!fullMap.has(fullKey)) fullMap.set(fullKey, r);
      const mokKey = `${chain.목}|${chain.세목}`;
      if (!mokMap.has(mokKey)) mokMap.set(mokKey, r);
      if (!semokMap.has(chain.세목)) semokMap.set(chain.세목, []);
      semokMap.get(chain.세목).push(r);
    });
  return { fullMap, mokMap, semokMap };
}

/** 사업등록 명칭 → 사업 행 (세부 > 단위 > 정책 우선) */
function buildProjectNameMap(projects) {
  const priority = { 세부: 0, 단위: 1, 정책: 2 };
  const map = new Map();
  (projects || [])
    .filter((r) => r.useYn !== 'N')
    .forEach((r) => {
      const key = normProjectName(r.projectName);
      if (!key) return;
      const prev = map.get(key);
      if (!prev || (priority[r.levelName] ?? 9) < (priority[prev.levelName] ?? 9)) {
        map.set(key, r);
      }
    });
  return map;
}

/** 레거시 사업명 말미 수식어 — 제거 후 사업등록 명칭과 재비교 */
const PROJECT_SUFFIX_RE = /(기획사업비|기획사업|사업비)$/;
/** 사업등록명이 레거시명의 접두일 때 허용하는 잔여 문구 */
const PREFIX_REMAINDER_RE = /^(기획사업비?|사업비|활동|지원)$/;

/** 사업 매칭 컨텍스트 — 이름 인덱스·레벨별 목록 */
function buildProjectMatchContext(projects) {
  const active = (projects || []).filter((r) => r.useYn !== 'N');
  return {
    projectMap: buildProjectNameMap(active),
    upperProjects: active
      .filter((r) => r.levelName === '단위' || r.levelName === '정책')
      .sort((a, b) => (a.levelName === b.levelName ? 0 : a.levelName === '단위' ? -1 : 1))
      .map((r) => ({ name: normProjectName(r.projectName), row: r }))
      .filter((e) => e.name),
    leafProjects: active
      .filter((r) => r.levelName !== '단위' && r.levelName !== '정책')
      .map((r) => ({ name: normProjectName(r.projectName), row: r }))
      .filter((e) => e.name),
    byCode: new Map(active.map((r) => [String(r.projectCode), r])),
  };
}

/**
 * 레거시 사업명(=레거시 목명) → 사업등록 행
 * 1) 정규화 일치 → 2) 말미 수식어·괄호 제거 → 3) 괄호 안 명칭 일치
 * 4) 정책·단위 사업과의 접두 관계
 * 5) 접두가 일치하는 세부사업들이 한 단위사업에 몰려 있으면 그 단위로 매핑
 */
function resolveProject(bizName, ctx) {
  const { projectMap, upperProjects, leafProjects, byCode } = ctx;
  const key = normProjectName(bizName);
  if (!key) return null;
  if (projectMap.has(key)) return projectMap.get(key);

  // "지역사회 지원(물품)" → 지역사회지원 · "…기획사업비" → 단위사업명
  const stripped = key.replace(/\([^()]*\)$/, '').replace(PROJECT_SUFFIX_RE, '');
  if (stripped && stripped !== key && projectMap.has(stripped)) {
    return projectMap.get(stripped);
  }

  // "대외협력사업(정책연구사업)" → 괄호 안이 사업등록 명칭
  const inner = /\(([^()]+)\)$/.exec(key)?.[1];
  if (inner && projectMap.has(norm(inner))) return projectMap.get(norm(inner));

  // 접두 비교는 4글자 이상일 때만 — "모금" 같은 짧은 어간의 우연 일치 방지
  const base = stripped || key;
  if (base.length < 4) return null;
  let hit = null;
  let hitKey = '';
  for (const { name, row } of upperProjects) {
    if (name.startsWith(base)) {
      // 레거시명이 단위사업명의 접두 — 예: "가정보호 및 대안양육" → "가정보호 및 대안양육 지원"
      if (!hit || name.length < hitKey.length) {
        hit = row;
        hitKey = name;
      }
    } else if (base.startsWith(name) && PREFIX_REMAINDER_RE.test(base.slice(name.length))) {
      // 단위사업명 + 허용 수식어 = 레거시명 — 예: "아동권리옹호" + "활동"
      if (!hit || name.length > hitKey.length) {
        hit = row;
        hitKey = name;
      }
    }
  }
  if (hit) return hit;

  // 세부사업 접두 후보 — 전부 같은 단위 아래면 그 단위로 (예: "위기가정아동 긴급지원" → 0201)
  const leafHits = leafProjects.filter((e) => e.name.startsWith(base));
  if (leafHits.length) {
    const parents = [...new Set(leafHits.map((e) => String(e.row.parentCode || '')))];
    if (parents.length === 1 && parents[0] && byCode.has(parents[0])) {
      return byCode.get(parents[0]);
    }
    if (leafHits.length === 1) return leafHits[0].row;
  }
  return null;
}

/**
 * GNK · 2026 · 본예산(100) 편성요구가 비어 있으면 이관 시드를 적재한다.
 * @returns {{ ok: boolean, seeded: boolean, count: number,
 *   unmatchedSubjects: string[], unmatchedProjects: number, fallbackSubjects: number }}
 */
export async function ensureGnkExpenseRequestBootstrap(dims) {
  const result = {
    ok: true,
    seeded: false,
    count: 0,
    unmatchedSubjects: [],
    unmatchedProjects: 0,
    fallbackSubjects: 0,
  };
  if (!isGnkTenant(dims?.tenantCode)) return result;
  if (String(dims?.fiscalYear || '') !== GNK_EXPENSE_FISCAL_YEAR) return result;
  if (String(dims?.budgetKindCode || '') !== MAIN_KIND_CODE) return result;

  const baseDims = {
    tenantCode: dims.tenantCode,
    fiscalYear: dims.fiscalYear,
    budgetKindCode: MAIN_KIND_CODE,
  };
  // 회계구분 미지정 조회 = 전체 라인 — 이미 데이터가 있으면 덮어쓰지 않는다.
  // 단, 손대지 않은 이관분(전 행 이관 마크)에 사업·재원 미지정이 남아 있으면
  // 기본 사업·재원을 채워 재적재한다.
  const existing = await loadExpenseRequests(baseDims);
  if (existing.length) {
    const pristineSeed = existing.every((r) =>
      String(r.remark || '').includes(GNK_EXPENSE_REQUEST_SOURCE)
    );
    const needsRefill = existing.some(
      (r) =>
        !String(r.projectCode || '').trim() ||
        (!r.fundUid && !r.fundCode && !r.fundPkgUid)
    );
    if (!pristineSeed || !needsRefill) {
      result.count = existing.length;
      return result;
    }
  }

  const catalogDims = {
    tenantCode: dims.tenantCode,
    fiscalYear: dims.fiscalYear,
    purpose: '집행',
    budgetType: BUDGET_TYPE_ALL,
    inoutDiv: '지출',
  };
  const subjects = (await loadSubjectsByFilter(catalogDims)) || [];
  const { fullMap, mokMap, semokMap } = buildSubjectChainMaps(subjects);
  const projectCtx = buildProjectMatchContext(await loadProjects(catalogDims));
  // 미매핑 행에 일괄 지정할 기본 사업
  const fallbackProject = projectCtx.projectMap.get(normProjectName(FALLBACK_PROJECT_NAME)) || null;
  // 기본 재원(국비) — 재원등록에서 이름으로 찾는다
  const funds = (await loadFunds(catalogDims)) || [];
  const defaultFund =
    funds.find((f) => f.useYn !== 'N' && norm(f.fundName) === norm(DEFAULT_FUND_NAME)) || null;
  const depts = (await loadDepts(dims.tenantCode)) || [];
  const deptByCode = new Map(depts.map((d) => [String(d.deptCode).toUpperCase(), d]));
  const deptByName = new Map(depts.map((d) => [norm(d.deptName), d]));

  const unmatchedSubjectSet = new Set();

  const masters = GNK_EXPENSE_REQUEST_ROWS.map((src) => {
    const dept =
      deptByCode.get(String(src.deptCode).toUpperCase()) ||
      deptByName.get(norm(src.deptName)) ||
      null;

    const fullKey = `${norm(src.sehangName)}|${norm(src.mokName)}|${norm(src.semokName)}`;
    const mokKey = `${norm(src.mokName)}|${norm(src.semokName)}`;
    let subject = fullMap.get(fullKey) || null;
    let subjectNote = '';
    if (!subject) {
      subject = mokMap.get(mokKey) || null;
      if (subject) {
        subjectNote = `과목 자동매핑(세항 상이: ${src.sehangName})`;
        result.fallbackSubjects += 1;
      }
    }
    if (!subject) {
      // 세목명이 카탈로그에서 유일하면 상위 체계가 달라도 매핑 (예: 주거환경개선)
      const candidates = semokMap.get(norm(src.semokName)) || [];
      if (candidates.length === 1) {
        subject = candidates[0];
        subjectNote = `과목 자동매핑(세목명 유일: ${src.sehangName}>${src.mokName})`;
        result.fallbackSubjects += 1;
      }
    }
    if (!subject) {
      unmatchedSubjectSet.add(`${src.sehangName}>${src.mokName}>${src.semokName}`);
    }

    let project = resolveProject(src.bizName, projectCtx);
    let projectNote = '';
    if (!project && fallbackProject) {
      // 이름 매핑 실패 행은 합의된 기본 사업으로 일괄 지정 — 레거시 명칭은 비고 보존
      project = fallbackProject;
      projectNote = `사업 기본지정(레거시: ${src.bizName})`;
      result.unmatchedProjects += 1;
    } else if (!project) {
      result.unmatchedProjects += 1;
    }

    const requestUid = createRequestUid();
    const remarks = [GNK_EXPENSE_REQUEST_SOURCE];
    if (subjectNote) remarks.push(subjectNote);
    if (!subject) remarks.push(`과목 미매핑: ${src.sehangName}>${src.mokName}>${src.semokName}`);
    if (projectNote) remarks.push(projectNote);
    else if (!project) remarks.push(`사업 미지정(레거시: ${src.bizName})`);

    return {
      requestUid,
      id: requestUid,
      deptCode: dept?.deptCode || src.deptCode,
      deptName: dept?.deptName || src.deptName,
      projectCode: project?.projectCode || '',
      projectName: project?.projectName || '',
      budgetType: subject?.budgetType || STRUCTURE_BUDGET_TYPE,
      subjectUid: subject?.subjectUid || '',
      subjectCode: subject?.subjectCode || '',
      subjectDispCode: subject ? buildSubjectDispCode(subject, subjects) : '',
      subjectName: subject
        ? buildSubjectDispName(subject, subjects)
        : `${src.sehangName} - ${src.semokName}`,
      fundUid: defaultFund?.fundUid || '',
      fundCode: defaultFund?.fundCode || '',
      fundName: defaultFund?.fundName || '',
      fundPkgUid: '',
      fundAmounts: defaultFund
        ? [{ fundUid: defaultFund.fundUid, ratioBp: 10000, amount: null }]
        : [],
      remark: remarks.join(' · '),
      details: (src.details || []).map((d) => {
        const detailId = createDetailId();
        return {
          detailId,
          id: detailId,
          calcLevel: d.calcLevel,
          calcDesc: d.calcDesc,
          calcBasis: d.calcBasis,
          calcAmt: d.calcAmt,
          remark: d.remark || '',
          basisLocked: false,
          mod: '',
        };
      }),
      mod: '',
    };
  });

  await saveExpenseRequests(
    { ...baseDims, acctClass: String(dims.acctClass || '') || defaultAcctClassCode(dims.tenantCode) },
    masters
  );
  result.seeded = true;
  result.count = masters.length;
  result.unmatchedSubjects = [...unmatchedSubjectSet];
  return result;
}
